import PacksList from "../../../../../components/packs/packsList/PacksList";
import NoResults from "../../../../../components/noResults/NoResults";
import { getPeoplePacksGridSortingOptions } from "./userPackGridSortingOptions";
import { type UserPacksState } from "../../../../Licensing/Packs/state/slices/userPacksGridSlice";
import { type UserPacksRequest } from "../../../../Licensing/Packs/types/requests";
import { type Filters } from "../../../../../utils/queryUtils";
import UserPacksUtils from "../../../../../utils/userPacksUtils";
import { type UserPacksGridFetchType } from "../../../../Licensing/Packs/state/thunks/userPacksGridThunk";
import ViewType from "../../../../../enums/ViewType";
import { type PackOverview } from "../../../../Licensing/Packs/types/state";
import UserPackListRow from "../../../../../components/packs/packsList/UserPackListRow";
import { RouteNames, type SortingDirection } from "../../../../../enums";
import {
  AddAllPackUserSuccess,
  AddAllUserLicenseMessageSuccess,
  RemoveAllUserLicenseMessageSuccess,
} from "../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import React from "react";
import { type sendTransientNotification } from "features/Notifications/state/notificationsActions";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { issueLicensePermissionPredicate } from "../issueLicensePermissionPredicate";
import { getUserPackColumnOptions } from "components/packs/packsList/getUserPackColumnOptions";

export interface IUserPacksActions {
  fetchPacksGrid: (requestData: UserPacksRequest) => UserPacksGridFetchType;
  resetPacksGrid: () => void;
}

export interface UserPacksProps {
  userId: number;
  accountId: number;
  packsActions: IUserPacksActions;
  packsGrid: UserPacksState;
  issueLicenseButton: React.ReactElement;
  sendTransientNotification: (
    payload: Parameters<typeof sendTransientNotification>[0],
    dismissInSeconds: number,
  ) => void;
  onRemoveLicenseClick: (id: number) => void;
  isReadOnly: boolean;
  selectedPackIds: number[];
  onSelectedPacksChanged: (ids: number[]) => void;
}

const rtnEvents = [AddAllPackUserSuccess, AddAllUserLicenseMessageSuccess, RemoveAllUserLicenseMessageSuccess];

const UserPacks = (props: UserPacksProps) => {
  const licenseRemovalEnabled = !!useFeatureFlag(FeatureFlags.LicenseRemoval);
  const columnOptions = getUserPackColumnOptions(licenseRemovalEnabled);

  const renderNoResults = () => (
    <NoResults
      title="This user does not have any pack licenses."
      description={
        <span>
          Looks like there are no pack licenses for this user yet. Issue a license now using the button below.
        </span>
      }
      iconClassName="fa-box-full"
    >
      {props.issueLicenseButton}
    </NoResults>
  );

  const loadPacksGrid = (
    skip: number,
    sortBy: string,
    sortDirection: SortingDirection,
    appliedFilter: Filters,
    searchTerm?: string,
    top?: number,
  ) => {
    const params = {
      userId: props.userId,
      accountId: props.accountId,
      top,
      skip,
      filters: appliedFilter,
      searchTerm,
    };

    const request: UserPacksRequest = { ...params, sortBy: UserPacksUtils.mapSortBy(sortBy), sortOrder: sortDirection };

    return props.packsActions.fetchPacksGrid(request);
  };

  const getListRow = (pack: PackOverview) => {
    const url = `/${RouteNames.purchasedPacks}/${pack.id}`;
    const isSelected = props.selectedPackIds.some((x) => x === pack.id);

    return (
      <UserPackListRow
        pack={pack}
        accountId={props.accountId}
        url={url}
        onRemoveLicenseClick={props.onRemoveLicenseClick}
        licenseRemovalEnabled={licenseRemovalEnabled}
        isSelected={isSelected}
      />
    );
  };

  return (
    <section className="scrollable-content">
      <PacksList
        viewType={ViewType.LIST}
        gridItems={props.packsGrid}
        getListRow={getListRow}
        columnOptions={columnOptions}
        loadGridItems={loadPacksGrid}
        resetGridItems={props.packsActions.resetPacksGrid}
        gridSortingOptions={getPeoplePacksGridSortingOptions()}
        renderNoResults={renderNoResults}
        refreshListEvents={rtnEvents}
        withSelection={licenseRemovalEnabled}
        isReadOnly={props.isReadOnly}
        selectedPackIds={props.selectedPackIds}
        onSelectedPacksChanged={props.onSelectedPacksChanged}
        packCardProps={{
          onRemoveLicenseClick: props.onRemoveLicenseClick,
          licenseRemovalEnabled,
          issueLicensePermissionPredicate,
        }}
      />
    </section>
  );
};

export default UserPacks;
